import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { extent, mean, sum } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { area, line } from 'd3-shape';
import { HookDirective } from '../core/hook';
import { useState } from '../core/hooks';
import { Size } from '../core/size.service';

@Component({
  selector: 'sparkline',
  templateUrl: './sparkline.component.html',
  styleUrls: ['./sparkline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparklineComponent<T> extends HookDirective {
  constructor(_cdr: ChangeDetectorRef) {
    super(_cdr);
  }

  @Input() data: T[];

  @Input() height: number = 96;

  @Input() xAccessor: (d: T) => number;

  @Input() yAccessor: (d: T) => number;

  /**
   * @param slope the slope of the line-of-best-fit
   * @returns a number; positive if the line should be rendered with the safety
   *   color (green), negative for the danger color (red), or 0 for neutral
   */
  @Input() trendInterpretation: (slope: number) => number = slope => slope;

  get _() {
    const VERTICAL_PADDING = 20;

    const [{ width, height }, setSize] = useState<Size>({
      width: 0,
      height: 0,
    });

    const xScale = scaleLinear()
      .domain(extent(this.data, this.xAccessor) as [number, number])
      .range([0, width]);
    const yScale = scaleLinear()
      .domain(extent(this.data, this.yAccessor) as [number, number])
      .range([height - VERTICAL_PADDING, VERTICAL_PADDING]);

    const path = line<T>()
      .x(d => xScale(this.xAccessor(d))!)
      .y(d => yScale(this.yAccessor(d))!)(this.data);
    const shadowPath = area<T>()
      .x(d => xScale(this.xAccessor(d))!)
      .y0(d => yScale(this.yAccessor(d))!)
      .y1(0)(this.data);

    const meanX = mean(this.data, this.xAccessor) || 0;
    const meanY = mean(this.data, this.yAccessor) || 0;
    const bestFitSlope =
      sum(
        this.data,
        d => (this.xAccessor(d) - meanX) * (this.yAccessor(d) - meanY),
      ) /
      sum(
        this.data,
        d => (this.xAccessor(d) - meanX) * (this.xAccessor(d) - meanX),
      );
    const interpretation = this.trendInterpretation(bestFitSlope);

    return { interpretation, path, shadowPath, setSize };
  }
}
